<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import DeviceCard from './DeviceCard.vue';

export default {
  name: 'NotificationDevices',
  components: {
    DeviceCard,
    Spinner,
  },
  data() {
    return {
      // NOTICE: KAPTION-INTEGRATION - State for proper delete confirmation modal (replaces window.confirm)
      showDeleteConfirmation: false,
      pendingDeleteId: null,
    };
  },
  computed: {
    ...mapGetters({
      subscriptions: 'notificationSubscriptions/getSubscriptions',
      uiFlags: 'notificationSubscriptions/getUIFlags',
      fcmSubscriptions: 'notificationSubscriptions/getFCMSubscriptions',
      browserSubscriptions: 'notificationSubscriptions/getBrowserSubscriptions',
      staleSubscriptions: 'notificationSubscriptions/getStaleSubscriptions',
    }),
  },
  mounted() {
    this.fetchSubscriptions();
  },
  methods: {
    async fetchSubscriptions() {
      try {
        await this.$store.dispatch('notificationSubscriptions/fetch');
      } catch (error) {
        useAlert(
          error.response?.data?.error ||
            error.message ||
            this.$t('PROFILE_SETTINGS.FORM.API.UPDATE_ERROR')
        );
      }
    },
    // NOTICE: KAPTION-INTEGRATION - Open delete confirmation modal instead of window.confirm
    openDeleteConfirmation(id) {
      this.pendingDeleteId = id;
      this.showDeleteConfirmation = true;
    },
    closeDeleteConfirmation() {
      this.showDeleteConfirmation = false;
      this.pendingDeleteId = null;
    },
    async confirmDeleteSubscription() {
      const id = this.pendingDeleteId;
      this.closeDeleteConfirmation();

      try {
        await this.$store.dispatch('notificationSubscriptions/delete', id);
        // NOTICE: KAPTION-INTEGRATION - Success toast after device deletion
        useAlert(this.$t('NOTIFICATION_DEVICES.DEVICE_CARD.DELETE_SUCCESS'));
      } catch (error) {
        useAlert(
          error.response?.data?.error ||
            error.message ||
            this.$t('PROFILE_SETTINGS.FORM.API.UPDATE_ERROR')
        );
      }
    },
    async testPush(id) {
      await this.$store.dispatch('notificationSubscriptions/testPush', id);
      // NOTICE: KAPTION-INTEGRATION - Success toast after test push
      const testResult =
        this.$store.getters['notificationSubscriptions/getTestResult'](id);
      if (testResult?.success) {
        useAlert(this.$t('NOTIFICATION_DEVICES.DEVICE_CARD.TEST_PUSH_SUCCESS'));
      }
    },
    async cleanupStale() {
      try {
        await Promise.all(
          this.staleSubscriptions.map(subscription =>
            this.$store.dispatch(
              'notificationSubscriptions/delete',
              subscription.id
            )
          )
        );
        // NOTICE: KAPTION-INTEGRATION - Success toast after stale cleanup
        useAlert(this.$t('NOTIFICATION_DEVICES.CLEANUP_SUCCESS'));
      } catch (error) {
        useAlert(
          error.response?.data?.error ||
            error.message ||
            this.$t('PROFILE_SETTINGS.FORM.API.UPDATE_ERROR')
        );
      }
    },
  },
};
</script>

<template>
  <section
    class="flex flex-col gap-4 p-4 border border-solid border-n-weak rounded-xl"
  >
    <div class="flex flex-col gap-1">
      <div class="flex items-center gap-2">
        <fluent-icon icon="globe" size="16" class="text-n-slate-12" />
        <h4 class="text-sm font-medium text-n-slate-12">
          {{ $t('NOTIFICATION_DEVICES.TITLE') }}
        </h4>
      </div>
      <p class="text-sm text-n-slate-11">
        {{ $t('NOTIFICATION_DEVICES.DESCRIPTION') }}
      </p>
    </div>

    <div v-if="uiFlags.isFetching" class="flex justify-center py-6">
      <Spinner class="text-n-brand" />
    </div>

    <template v-else>
      <div
        v-if="staleSubscriptions.length"
        class="flex flex-col gap-3 p-3 rounded-xl border border-n-amber-6 bg-n-amber-2 sm:flex-row sm:items-center sm:justify-between"
      >
        <div class="flex items-start gap-2">
          <fluent-icon
            icon="warning"
            size="16"
            class="mt-0.5 text-n-amber-12 flex-shrink-0"
          />
          <span class="text-sm text-n-amber-12">
            {{
              $t('NOTIFICATION_DEVICES.STALE_WARNING', {
                count: staleSubscriptions.length,
              })
            }}
          </span>
        </div>
        <woot-button
          size="small"
          variant="clear"
          :is-loading="uiFlags.isDeleting"
          @click="cleanupStale"
        >
          {{ $t('NOTIFICATION_DEVICES.CLEANUP_STALE') }}
        </woot-button>
      </div>

      <div v-if="fcmSubscriptions.length" class="flex flex-col gap-3">
        <div class="flex items-center gap-2">
          <fluent-icon icon="phone" size="16" class="text-n-slate-12" />
          <span class="text-sm font-medium text-n-slate-12">
            {{ $t('NOTIFICATION_DEVICES.FCM_GROUP') }}
          </span>
        </div>

        <DeviceCard
          v-for="subscription in fcmSubscriptions"
          :key="subscription.id"
          :subscription="subscription"
          :testing="uiFlags.isTesting[subscription.id]"
          :test-result="
            $store.getters['notificationSubscriptions/getTestResult'](
              subscription.id
            )
          "
          @test="testPush(subscription.id)"
          @delete="openDeleteConfirmation(subscription.id)"
        />
      </div>

      <div v-if="browserSubscriptions.length" class="flex flex-col gap-3">
        <div class="flex items-center gap-2">
          <fluent-icon icon="globe" size="16" class="text-n-slate-12" />
          <span class="text-sm font-medium text-n-slate-12">
            {{ $t('NOTIFICATION_DEVICES.BROWSER_GROUP') }}
          </span>
        </div>

        <DeviceCard
          v-for="subscription in browserSubscriptions"
          :key="subscription.id"
          :subscription="subscription"
          :testing="uiFlags.isTesting[subscription.id]"
          :test-result="
            $store.getters['notificationSubscriptions/getTestResult'](
              subscription.id
            )
          "
          @test="testPush(subscription.id)"
          @delete="openDeleteConfirmation(subscription.id)"
        />
      </div>

      <div
        v-if="!subscriptions.length"
        class="p-4 text-sm text-n-slate-11 bg-n-alpha-2 rounded-xl"
      >
        {{ $t('NOTIFICATION_DEVICES.EMPTY') }}
      </div>
    </template>

    <!-- NOTICE: KAPTION-INTEGRATION - Proper delete confirmation modal instead of window.confirm -->
    <woot-delete-modal
      v-model:show="showDeleteConfirmation"
      :on-close="closeDeleteConfirmation"
      :on-confirm="confirmDeleteSubscription"
      :title="$t('NOTIFICATION_DEVICES.DEVICE_CARD.DELETE_CONFIRM_TITLE')"
      :message="$t('NOTIFICATION_DEVICES.DEVICE_CARD.DELETE_CONFIRM')"
      :confirm-text="$t('NOTIFICATION_DEVICES.DEVICE_CARD.DELETE_CONFIRM_YES')"
      :reject-text="$t('NOTIFICATION_DEVICES.DEVICE_CARD.DELETE_CONFIRM_NO')"
    />
  </section>
</template>
